<template>
  <div
    class="delete-account"
  >
    <ui-button
      v-if="showDeleteAccountButton"
      type="button"
      button-type="blank"
      size="small"
      @click="onDelete"
      :label="i18n.deleteAccount"
      normal-case
    >
      <template #icon-left>
        <svg-trash-icon/>
      </template>
    </ui-button>
    <div
      v-if="showSendingEmailTextSection"
      class="d-flex"
    >
      <span
        class="mr-3"
      >
        {{ i18n.sendingEmail }}
      </span>
      <spinner
        width="20"
        height="20"
      />
    </div>

    <div
      v-if="showEmailSentTextSection"
      class="d-flex flex-column justify-content-between max-w-360-px"
    >
      <div
        class="d-flex align-items-center"
      >
        <p
          class="text-gray-800 text-sm font-weight-semibold"
        >
          <svg-success
            color="green"
          />
          {{ i18n.emailSent }}
        </p>
      </div>
      <span
        class="text-gray-800 text-sm font-weight-normal"
        id="delete-account__sent-confirmation-email-text"
        v-html="i18n.sentConfirmationEmailText"
      />
      <p
        v-if="showResendLinkText"
        class="text-gray-800 text-sm font-weight-semibold"
      >
        {{ i18n.didNotReceiveEmailText }}
        <ui-button
          type="button"
          button-type="blank"
          size="small"
          font-weight="semibold"
          @click="resendLink"
          :label="i18n.sendNewLinkText"
          normal-case
          class="px-0"
        />
      </p>
    </div>

    <div
      v-if="showFailDeliverySection"
      class="d-flex flex-column justify-content-between max-w-360-px"
    >
      <div
        class="d-flex align-items-center"
      >
        <p
          class="text-gray-800 text-sm font-weight-semibold"
        >
          <svg-error/>
          {{ i18n.emailDeliveryFailedTitle }}
        </p>
      </div>
      <p
        class="text-gray-800 text-sm font-weight-normal"
        v-html="i18n.emailDeliveryFailedMessage"
      />
      <p
        class="text-gray-800 text-sm font-weight-normal"
        v-text="i18n.contactSupport"
      />
      <p
        class="text-gray-800 text-sm font-weight-semibold"
      >
        {{ i18n.didNotReceiveEmailText }}

        <ui-button
          type="button"
          button-type="blank"
          size="small"
          font-weight="semibold"
          @click="resendLink"
          :label="i18n.resendLinkText"
          class="px-0"
          normal-case
        />
      </p>
    </div>
  </div>
</template>

<script>
// import Translations from "./insert-me-to-translate.vue";

const COOKIE_NAME = 'delete_account_sent';
export default {
  components: {
    UiButton: () => import(/* webpackChunkName: "chunk-ui-button" */ './ui/ui-button.vue'),
    SvgTrashIcon: () => import(/* webpackChunkName: "chunk-svg-trash-icon" */ './svg/trash.vue'),
    Spinner: () => import(/* webpackChunkName: "chunk-spinner" */ './ui/spinner.vue'),
    SvgSuccess: () => import(/* webpackChunkName: "chunk-svg-success" */ './svg/success.vue'),
    SvgError: () => import(/* webpackChunkName: "chunk-svg-error" */ './svg/error.vue')
  },
  props: {
    user: {
      type: String,
      required: true
    },
    resendDeleteEmailUrl: {
      type: String,
      required: true
    },
    email: {
      type: String,
      required: true
    },
    userHashedEmail: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      showDeleteAccountButton: true,
      showSendingEmailText: false,
      showEmailSentText: false,
      showResendLinkText: false,
      showFailDeliveryText: false,
      i18n: {
        deleteAccount: gettext('Delete my account'),
        sendingEmail: gettext('Sending email...'),
        emailSent: gettext('Email sent'),
        sentConfirmationEmailText: interpolate(
          gettext("We’ve sent a confirmation link to <span>%s</span>. " +
            "You will have 1 hour to complete your account deletion. If you need a new link, please wait until this period has expired before requesting it again. " +
            "If you didn't find it in your inbox, check your <strong>spam</strong> — it might be hiding there."),
          [this.email]),
        didNotReceiveEmailText: gettext("Didn't receive the email?"),
        sendNewLinkText: gettext('Send me a new link'),
        emailDeliveryFailedTitle: gettext('Oops! Email delivery failed'),
        emailDeliveryFailedMessage: interpolate(
          gettext("We couldn't deliver your confirmation link to <strong>%s</strong>, but encountered a technical issue. " +
            "Please try resending the link in a few minutes."), [this.email]),
        contactSupport: gettext('If the problem persists, contact support.'),
        resendLinkText: gettext('Resend link')
      }
    }
  },
  computed: {
    showSendingEmailTextSection() {
      return !this.showEmailSentText && this.showSendingEmailText
    },
    showEmailSentTextSection() {
      return this.showEmailSentText && !this.showSendingEmailText
    },
    showFailDeliverySection() {
      return this.showFailDeliveryText && !this.showSendingEmailText && !this.showEmailSentText
    }
  },
  mounted() {
    //below code is to check if the cookie exists and set the texts accordingly
    const cookieValues = this.$cookies.get(`${COOKIE_NAME}_${this.userHashedEmail}`);

    if (cookieValues) {
      this.showDeleteAccountButton = false;
      const params = new URLSearchParams(cookieValues);
      const sentEmail = params.get('sent_email');
      const canRetry = params.get('can_retry');

      this.showEmailSentText = (sentEmail === '1');
      this.showResendLinkText = (canRetry === '1');
    }
  },
  methods: {
    async onDelete() {
      this.showDeleteAccountButton = false;
      this.showSendingEmailText = true;

      try {
        const result = await this.sendEmail();
        this.showSendingEmailText = false;

        if (result.success) {
          this.setCookie(1, 1, result.maxAgeInSeconds);

          this.showEmailSentText = true;
          this.showResendLinkText = true;
        }
      } catch (error) {
        console.error('send delete email failed:', error);
        this.showSendingEmailText = false;
      }
    },

    async sendEmail() {
      try {
        this.showFailDeliveryText = false;

        const response = await this.$http.post(
          this.resendDeleteEmailUrl,
          {},
          {
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            }
          }
        );

        if (response.status === 200) {
          const oneHourInSeconds = 3600; // 1 hour in seconds
          let maxAgeInSeconds;

          if (response?.data?.expires_at) {
            // calculate max‐age for the cookie based on the expiration date
            const expirationDate = new Date(response.data.expires_at);
            const now = new Date();
            maxAgeInSeconds = Math.floor(
              (expirationDate.getTime() - now.getTime()) / 1000
            );
          } else {
            // Default to 1 hour if no expiration date is provided
            maxAgeInSeconds = oneHourInSeconds;
          }
          return {success: true, maxAgeInSeconds};
        } else {
          return {success: false};
        }
      } catch (error) {
        console.error('error with request', error);
        this.showFailDeliveryText = true;
        return {success: false};
      }
    },

    async resendLink() {
      this.showSendingEmailText = true;
      this.showEmailSentText = false;
      try {
        const result = await this.sendEmail();
        this.showSendingEmailText = false;

        if (result.success) {
          this.setCookie(1, 0, result.maxAgeInSeconds);

          this.showEmailSentText = true;
          this.showResendLinkText = false;
        }
      } catch (error) {
        console.error('resendLink failed:', error);
        this.showSendingEmailText = false;
      }
    },


    setCookie(sentEmail, canRetry, maxAgeInSeconds) {
      const cookieParams = new URLSearchParams();
      cookieParams.set('sent_email', sentEmail);
      cookieParams.set('can_retry', canRetry);


      this.$cookies.set(
        `${COOKIE_NAME}_${this.userHashedEmail}`,
        cookieParams.toString(),
        maxAgeInSeconds,
        null,
        locations.cookie_domain
      );
    }
  }
};

</script>

<style>
#delete-account__sent-confirmation-email-text span {
  word-wrap: break-word;
  word-break: break-word;
}
</style>
